import { motion } from "framer-motion";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { FriendReferralForm } from "@/components/FriendReferralForm";
import { Brain, HeartHandshake, Waves, Gem, Link2, LifeBuoy, Hand, Share2, CalendarCheck } from "lucide-react";

const CALENDLY_URL = "https://calendly.com/divinelovewellnessenterprises/consultationmeeting";

const benefits = [
  { icon: Brain, title: "Enhanced Neuroplasticity & Breakthroughs", body: "Low-dose ketamine gently quiets the brain’s default mode network (DMN)—the region responsible for repetitive self-criticism, anxiety, and rigid emotional defenses—making deep emotional shifts more accessible." },
  { icon: HeartHandshake, title: "Deep Relational Healing", body: "Witnessing others navigate their inner worlds instantly dissolves shame. You realize you are never alone in your struggles, your fears, or your hopes." },
  { icon: Waves, title: "Amplified Emotional Resonance", body: "The shared energy of a supportive, trauma-informed group container helps regulate the nervous system faster and more deeply than individual work alone." },
  { icon: Gem, title: "Affordable, High-Level Care", body: "Experience intensive, transformative psychedelic psychotherapy in an accessible, community-oriented format." },
];

const friendPoints = [
  { icon: Link2, title: "Deepen Your Bond", body: "Step past everyday routines into profound, soulful connection." },
  { icon: LifeBuoy, title: "A Built-in Support System", body: "Maintain your integration momentum and process your insights long after the session ends with someone who was right there beside you." },
  { icon: Hand, title: "Courage in Community", body: "Stepping into the unknown feels infinitely safer and more grounded when you have a familiar, trusted hand to hold." },
];

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6 },
};

const PointCard = ({ item, testId }) => (
  <div className="flex items-start gap-4 p-5 md:p-6 bg-white border border-border/40 rounded-2xl" data-testid={testId}>
    <div className="p-3 rounded-full bg-primary/10 flex-shrink-0">
      <item.icon className="h-5 w-5 text-primary" />
    </div>
    <div>
      <h4 className="font-serif text-lg font-semibold text-foreground">{item.title}</h4>
      <p className="text-sm md:text-base text-muted-foreground mt-1 leading-relaxed">{item.body}</p>
    </div>
  </div>
);

const sharePage = async () => {
  const url = window.location.href;
  if (navigator.share) {
    try {
      await navigator.share({ title: document.title, url });
    } catch {
      /* user dismissed */
    }
    return;
  }
  await navigator.clipboard.writeText(url);
  toast.success("Link copied — send it to a friend!");
};

export const GroupKapNarrative = () => (
    <div className="mt-10" data-testid="group-kap-narrative">
      <motion.div {...fadeUp}>
        <h2 className="font-serif text-2xl sm:text-3xl md:text-4xl font-semibold text-foreground leading-tight">
          Group Ketamine-Assisted Psychotherapy <span className="italic text-primary">(KAP)</span>
        </h2>
        <h3 className="mt-4 font-serif text-xl sm:text-2xl lg:text-3xl italic font-medium text-primary leading-snug">
          Healing is Not Meant to Be Done in Isolation
        </h3>
        <div className="mt-8 text-base md:text-lg text-foreground/80 leading-[1.85]">
          <p className="mb-5">
            While individual therapy offers a vital space for personal introspection, deep relational wounds are often healed in the presence of others. Group Ketamine-Assisted Psychotherapy (KAP) combines the neuroplastic, nervous-system-softening benefits of medicine with the profound power of collective community.
          </p>
          <p>
            In a carefully curated, safe group container, we move beyond surface-level conversation into shared vulnerability, profound empathy, and collective transformation.
          </p>
        </div>
      </motion.div>

      <motion.div {...fadeUp} className="mt-16">
        <h2 className="font-serif text-2xl sm:text-3xl font-semibold text-foreground leading-tight">
          The Benefits of <span className="italic text-primary">Group KAP</span>
        </h2>
        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
          {benefits.map((b, i) => (
            <PointCard key={b.title} item={b} testId={`group-kap-benefit-${i + 1}`} />
          ))}
        </div>
      </motion.div>

      <motion.div {...fadeUp} className="mt-16 rounded-3xl border border-primary/15 bg-secondary/40 px-5 md:px-8 py-8 md:py-10" data-testid="group-kap-bring-friend">
        <h3 className="font-serif text-2xl md:text-3xl font-semibold text-foreground leading-tight">
          Better Together: <span className="italic text-primary">Bring a Friend</span>
        </h3>
        <p className="mt-4 text-base md:text-lg text-foreground/80 leading-[1.85]">
          Psycho-spiritual exploration and integration are exponentially more powerful when shared with someone who truly understands your world. We warmly invite you to bring a friend, partner, or trusted anchor into this experience with you.
        </p>
        <div className="mt-8 flex flex-col gap-4">
          {friendPoints.map((f, i) => (
            <PointCard key={f.title} item={f} testId={`group-kap-friend-point-${i + 1}`} />
          ))}
        </div>
        <FriendReferralForm />
      </motion.div>

      <motion.div {...fadeUp} className="mt-16" data-testid="group-kap-ready">
        <h3 className="font-serif text-2xl md:text-3xl font-semibold text-foreground leading-tight">
          Ready to <span className="italic text-primary">Transform Together?</span>
        </h3>
        <p className="mt-4 text-base md:text-lg text-muted-foreground leading-relaxed">
          Whether you are looking to deepen an existing friendship or share a milestone of personal growth, our upcoming group cohorts offer a compassionate, secure space for profound healing.
        </p>
        <p className="mt-8 font-serif text-lg font-semibold text-foreground">Take the First Step:</p>
        <div className="mt-5 flex flex-col sm:flex-row sm:flex-wrap items-start gap-4">
          <Button
            variant="outline"
            onClick={sharePage}
            className="rounded-full px-8 py-6 text-base border-primary/30 text-primary hover:bg-primary/5"
            data-testid="group-kap-share-btn"
          >
            <Share2 className="mr-2 h-4 w-4" />
            Share this page with a friend
          </Button>
          <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer" data-testid="group-kap-intake-link">
            <Button className="rounded-full px-8 py-6 text-base bg-primary text-primary-foreground hover:bg-primary/90">
              <CalendarCheck className="mr-2 h-4 w-4" />
              Schedule a Group Intake Consultation
            </Button>
          </a>
        </div>
        <p className="mt-5 text-sm text-muted-foreground">
          Learn more about our upcoming cohorts and see if Group KAP is the right fit for you.
        </p>
      </motion.div>
    </div>
);
